import React, { useState, useRef, useEffect } from 'react';
import { useSidebar } from '../../SidebarContext';
import {
    Mic, Send, Lightbulb, Plus, Image, X,
    RefreshCw, CheckCircle, AlertCircle,
    UploadCloud,
    History,
    ThumbsUp,
    ThumbsDown,
    Save,
} from 'lucide-react';
import axios from 'axios';
import gsap from "gsap";
import { useNavigate } from 'react-router-dom';
import { asRenderableText } from '../utils/richTextForQuill';

const API_BASE_URL = 'https://api.beentara.com/api';

const ProductAi = () => {
    
    const [inputValue, setInputValue] = useState('');
    const [selectedImage, setSelectedImage] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [chatStarted, setChatStarted] = useState(false);
    const [showOptions, setShowOptions] = useState(false);
    const [isMultiLine, setIsMultiLine] = useState(false);
    const [messages, setMessages] = useState([]);
    const [isTyping, setIsTyping] = useState(false);
    const [productData, setProductData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState('');
    const [saveError, setSaveError] = useState('');
    const [isImageSent, setIsImageSent] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [generationStep, setGenerationStep] = useState(0);
    const stepRef = useRef(null);
    const navigate = useNavigate()
    const [formActive, setFormActive] = useState(false);


    const generationSteps = [
        "Analyzing product image…",
        "Generating product title…",
        "Generating short description…",
        "Generating long description…",
        "Generating key features…",
        "Generating specifications…",
    ];

    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);
    const chatEndRef = useRef(null);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isTyping]);

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setSelectedImage(URL.createObjectURL(file));

            setTimeout(() => {
                textareaRef.current?.focus();
            }, 0);
        }
    };

    const removeImage = () => {
        setSelectedImage(null);
        setImageFile(null);
        setIsImageSent(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    useEffect(() => {
        if (!isTyping || !stepRef.current) return;

        gsap.fromTo(
            stepRef.current,
            { opacity: 0, y: 4 },
            {
                opacity: 1,
                y: 0,
                duration: 0.4,
                ease: "power2.out"
            }
        );
    }, [generationStep, isTyping]);

    const sendMessage = async () => {
        if (!inputValue.trim() && !imageFile) return;

        const userMsg = {
            id: Date.now(),
            text: inputValue,
            image: selectedImage, // Show in chat
            sender: 'user'
        };

        // Add user message to chat
        setMessages(prev => [...prev, userMsg]);
        setChatStarted(true)

        setInputValue('');
        setIsImageSent(true);
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
        }
        setIsMultiLine(false);

        setIsTyping(true);
        setSaveError('');
        setSaveSuccess('');

        setIsTyping(true);
        setGenerationStep(0);

        try {
            const fd = new FormData();
            if (imageFile) fd.append('image', imageFile);
            if (inputValue.trim()) fd.append('user_instructions', inputValue.trim());

            const res = await axios.post(`${API_BASE_URL}/ai/generate-product-info`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 60000,
                onUploadProgress: () => {
                    let step = 0;
                    const interval = setInterval(() => {
                        step++;
                        if (step < generationSteps.length) {
                            setGenerationStep(step);
                        } else {
                            clearInterval(interval);
                        }
                    }, 1200);
                }
            });

            if (res.data.success) {
                const aiData = res.data.data;
                setProductData(aiData);

                setMessages(prev => [
                    ...prev,
                    {
                        id: Date.now() + 1,
                        product: aiData,
                        sender: 'admin',
                        isStructured: true
                    }
                ]);
            } else {
                throw new Error(res.data.message || 'AI failed');
            }
        } catch (err) {
            const msg = err.response?.data?.message || err.message || 'AI error';
            setMessages(prev => [
                ...prev,
                { id: Date.now() + 1, text: `Error: ${msg}`, sender: 'admin', isError: true }
            ]);
        } finally {
            setIsTyping(false);
            setGenerationStep(0);
        }
    };

    const regenerateField = async (field) => {
        if (!imageFile) return;
        if (field === 'all') {
            setTimeout(() => {
                textareaRef.current?.focus();
            }, 0);
        }
        setIsTyping(true);
        try {
            const fd = new FormData();
            fd.append('image', imageFile);
            fd.append('fields', JSON.stringify([field]));

            const res = await axios.post(`${API_BASE_URL}/ai/generate-product-info`, fd, {
                timeout: 60000
            });

            if (res.data.success) {
                const updated = res.data.data;
                setProductData(prev => ({ ...prev, [field]: updated[field] }));

                setMessages(prev => [
                    ...prev,
                    {
                        id: Date.now(),
                        text: `Regenerated *${field.replace('_', ' ')}*`,
                        sender: 'admin',
                        isRegen: true
                    }
                ]);
            }
        } catch (err) {
            setMessages(prev => [
                ...prev,
                { id: Date.now(), text: 'Regeneration failed', sender: 'admin', isError: true }
            ]);
        } finally {
            setIsTyping(false);
        }
    };

    const saveProduct = async () => {
        if (!productData || !imageFile) return;
        setIsSaving(true);
        setSaveError('');
        setSaveSuccess('');

        try {
            const fd = new FormData();
            fd.append('title', productData.title);
            fd.append('short_description', productData.short_description);
            fd.append('long_description', productData.long_description);
            fd.append('key_features', JSON.stringify(productData.key_features));
            fd.append('specifications', JSON.stringify(productData.specifications));
            fd.append('price', productData.price || '');
            fd.append('warranty', productData.warranty || '');
            fd.append('image', imageFile);

            const res = await axios.post(`${API_BASE_URL}/products/save`, fd, {
                timeout: 60000
            });

            if (res.data.success) {
                setSaveSuccess('Product saved successfully!');
                setTimeout(() => {
                    setProductData(null);
                    setMessages([]);
                    setChatStarted(false);
                    removeImage();
                    setSaveSuccess('');
                }, 2000);
            }
        } catch (err) {
            setSaveError(err.response?.data?.message || 'Save failed');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            setImageFile(file);
            setSelectedImage(URL.createObjectURL(file));
            setChatStarted(true);


            setTimeout(() => {
                textareaRef.current?.focus();
            }, 0);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        if (saveSuccess || saveError) {
            const timer = setTimeout(() => {
                setSaveSuccess('');
                setSaveError('');
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [saveSuccess, saveError]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest(".options-menu") && !e.target.closest(".options-toggle")) {
                setShowOptions(false);
            }
        };

        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);



    return (
        <>
            {/* Welcome UI before chat starts */}
            {!chatStarted && (
                <div className="flex flex-col items-center justify-center text-center px-2 md:px-0 py-10 select-none animate-fadeIn">

                    {/* Icon container */}
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg mb-4">
                        <Lightbulb className="text-white" size={28} />
                    </div>

                    {/* Title */}
                    <h2 className="text-2xl font-extrabold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                        Product AI
                    </h2>

                    {/* Subtitle */}
                    <p className="text-sm text-gray-500 mt-2 max-w-sm leading-relaxed">
                        Upload a product image or ask anything to get instant insights,
                        suggestions & smart recommendations.
                    </p>

                    <div
                        onClick={() => {
                            fileInputRef.current?.click();
                            setShowOptions(false);
                        }}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        className={`
        p-6 border border-dashed 
        rounded-xl max-w-[480px] mx-auto mt-4 w-full
        flex flex-col items-center justify-center gap-2
        cursor-pointer transition text-center
        ${isDragging
                                ? "border-blue-600 bg-blue-100 text-blue-700 scale-105"
                                : "border-blue-500 hover:bg-blue-50 text-blue-600"
                            }
    `}
                    >
                        <UploadCloud className="w-10 h-10 mb-1" />

                        <h3 className="text-sm font-semibold">
                            Drag & Drop to Upload
                        </h3>

                        <p className="text-xs text-blue-500">
                            or click to browse files
                        </p>
                    </div>

                </div>
            )}

            <div className="px-2 md:pb-20 flex flex-col gap-3 overflow-y-auto max-w-[950px] mx-auto">
                {messages.map((msg) => (
                    <div
                        key={msg.id}
                        className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                    >

                        <div
                            className={`max-w-[85%] p-1.5 md:p-2 rounded-xl ${msg.sender === 'user'
                                ? 'bg-blue-50 text-black'
                                : msg.isError
                                    ? 'bg-red-100 text-red-700'
                                    : 'text-gray-800'
                                }`}
                        >
                            {msg.image && (
                                <img
                                    src={msg.image}
                                    alt="Upload"
                                    className="w-full max-w-32 rounded-lg "
                                />
                            )}

                            {msg.text && (
                                <p className="text-sm whitespace-pre-wrap leading-relaxed">
                                    {msg.text}
                                </p>
                            )}
                            {msg.isStructured && msg.product && (
                                <>
                                    <ul className="mt-3 space-y-4 text-sm">
                                        {/* Title */}
                                        <li className="flex items-start flex-col mt-5">
                                            <span className="text-base font-semibold">Title:</span>
                                            <div className="flex-1 flex items-center gap-2 text-base font-medium">
                                                <span>{msg.product.title || '—'}</span>
                                                <button
                                                    onClick={() => regenerateField('title')}
                                                    className="text-purple-600 hover:text-purple-800"
                                                    title="Regenerate title"
                                                >
                                                    <RefreshCw size={14} />
                                                </button>
                                            </div>
                                        </li>

                                        {/* Short Description */}
                                        <li className="flex items-start flex-col mt-5">
                                            <span className="font-semibold text-base">Short Description:</span>
                                            <div className="flex-1 flex items-center gap-2 ">
                                                <span className='text-base font-medium'>{msg.product.short_description || '—'}</span>
                                                <button
                                                    onClick={() => regenerateField('short_description')}
                                                    className="text-purple-600 hover:text-purple-800"
                                                    title="Regenerate short description"
                                                >
                                                    <RefreshCw size={14} />
                                                </button>
                                            </div>
                                        </li>

                                        {/* Long Description */}
                                        <li className="flex items-start flex-col mt-5">
                                            <span className="font-semibold text-base">Long Description:</span>
                                            <div className="flex-1 flex items-center gap-2">
                                                <span className='text-base font-medium'>{msg.product.long_description || '—'}</span>
                                                <button
                                                    onClick={() => regenerateField('long_description')}
                                                    className="text-purple-600 hover:text-purple-800"
                                                    title="Regenerate long description"
                                                >
                                                    <RefreshCw size={14} />
                                                </button>
                                            </div>
                                        </li>

                                        {/* Key Features */}
                                        {msg.product.key_features?.length > 0 && (
                                            <li className="flex items-start flex-col mt-5">
                                                <span className="font-semibold text-base">Key Features:</span>
                                                <div className="flex-1 text-right flex items-center gap-2">
                                                    <ul className="list-disc list-inside text-left text-base font-medium">
                                                        {msg.product.key_features.map((f, i) => (
                                                            <li key={i}>{asRenderableText(f) || "—"}</li>
                                                        ))}
                                                    </ul>
                                                    <button
                                                        onClick={() => regenerateField('key_features')}
                                                        className="text-purple-600 hover:text-purple-800"
                                                        title="Regenerate key features"
                                                    >
                                                        <RefreshCw size={14} />
                                                    </button>
                                                </div>
                                            </li>
                                        )}

                                        {msg.product.specifications && Object.keys(msg.product.specifications).length > 0 && (
                                            <li className="flex items-start flex-col mt-8">
                                                <span className="font-semibold text-base">Specifications:</span>
                                                <div className="flex-1 flex items-center gap-2">
                                                    <div className="space-y-1 text-left text-base font-medium">
                                                        {Object.entries(msg.product.specifications).map(([k, v]) => (
                                                            <div key={k} className="flex">
                                                                <span>{k}:</span>
                                                                <span className="font-medium ml-2">{v}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <button
                                                        onClick={() => regenerateField('specifications')}
                                                        className="text-purple-600 hover:text-purple-800"
                                                        title="Regenerate specifications"
                                                    >
                                                        <RefreshCw size={14} />
                                                    </button>
                                                </div>
                                            </li>
                                        )}

                                        {/* Price */}
                                        <li className="flex items-start flex-col md:flex-row">
                                            <span className="font-medium">Price:</span>
                                            <span className="flex-1 ml-2 flex items-center gap-2">{msg.product.price ? `$${msg.product.price}` : '—'}</span>
                                        </li>

                                        {/* Warranty */}
                                        <li className="flex items-start flex-col md:flex-row">
                                            <span className="font-medium">Warranty:</span>
                                            <span className="flex-1 ml-2 flex items-center gap-2">{msg.product.warranty || '—'}</span>
                                        </li>
                                    </ul>
                                    {/* Action icons row */}
                                    <div className="mt-2 flex items-center gap-1">

                                        {/* Like */}
                                        <button
                                            className="hover:bg-gray-100 p-1 rounded-full"
                                            title="Like"
                                        >
                                            <ThumbsUp size={18} />
                                        </button>

                                        {/* Dislike */}
                                        <button
                                            className="hover:bg-gray-100 p-1 rounded-full"
                                            title="Dislike"
                                        >
                                            <ThumbsDown size={18} />
                                        </button>

                                        {/* Regenerate */}
                                        <button
                                            onClick={() => regenerateField('all')}
                                            className="hover:bg-gray-100 p-1 rounded-full"
                                            title="Regenerate all"
                                        >
                                            <RefreshCw size={18} />
                                        </button>

                                        {/* Save */}
                                        <button
                                            onClick={saveProduct}
                                            disabled={isSaving}
                                            className="bg-gradient-to-tr from-blue-400 to-blue-600 hover:from-blue-700 hover:to-blue-900 text-white px-3 py-1 rounded-full flex items-center gap-1 text-sm transition"
                                            title="Continue details"
                                        >
                                            <Save size={18} /> Continue
                                        </button>

                                    </div>

                                </>
                            )}
                        </div>
                    </div>
                ))}

                {isTyping && (
                    <div
                        ref={stepRef}
                        className="self-start flex items-center gap-3  text-blue-700  animate-fadeIn"
                    >
                        {/* Rounded Spinner */}
                        <div className="relative w-5 h-5">
                            <div className="absolute inset-0 rounded-full border-2 border-blue-200"></div>
                            <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-blue-600 animate-spin"></div>
                        </div>

                        {/* Step Text */}
                        <span className="text-sm font-medium">
                            {generationSteps[generationStep]}
                        </span>
                    </div>
                )}
                {/* ✅ Scroll Anchor */}
                <div ref={chatEndRef}></div>

                {/* Save Success / Error */}
                {saveSuccess && (
                    <div className="fixed top-4 left-1/2 -translate-x-1/2 bg-green-100 text-green-700 px-4 py-2 rounded-lg flex items-center gap-2 z-50">
                        <CheckCircle size={18} />
                        {saveSuccess}
                    </div>
                )}

                {saveError && (
                    <div className="fixed top-4 left-1/2 -translate-x-1/2 bg-red-100 text-red-700 px-4 py-2 rounded-lg flex items-center gap-2 z-50">
                        <AlertCircle size={18} />
                        {saveError}
                    </div>
                )}
            </div>



            <form onSubmit={(e) => {
                e.preventDefault();
                sendMessage();
            }} className="fixed bottom-0 pb-4 bg-white product-ai">
                <div className='max-w-[1000px] mx-auto'>
                    {selectedImage && !isImageSent && (
                        <div className="mb-2 p-2 bg-white border rounded-xl shadow relative inline-block">
                            <img
                                src={selectedImage}
                                alt="Preview"
                                className="max-w-full max-h-32 object-cover rounded-lg"
                            />

                            <button
                                type="button"
                                onClick={removeImage}
                                className="absolute top-1 right-1 bg-black/70 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-black transition"
                            >
                                <X />
                            </button>
                        </div>
                    )}

                    <div className={`flex ${isMultiLine ? "items-end" : "items-center"} 
    bg-white shadow-sm rounded-xl p-2 w-full gap-2 border border-gray-300`}
                    >

                        {/* Dropdown */}
                        <button
                            type="button"
                            onClick={() => setShowOptions(!showOptions)}
                            className="p-2 hover:bg-gray-100 rounded-lg options-toggle"
                        >
                            <Plus size={18} />
                        </button>

                        <textarea
                            ref={textareaRef}
                            value={inputValue}
                            onChange={(e) => {
                                setInputValue(e.target.value);

                                // Auto-expand textarea height
                                e.target.style.height = "auto";
                                e.target.style.height = e.target.scrollHeight + "px";

                                setIsMultiLine(e.target.scrollHeight > 28);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();

                                    // ✅ Check send condition FIRST — DO NOT let resize block this
                                    if (!inputValue.trim() && !imageFile) return;

                                    sendMessage();

                                    // ✅ Move this AFTER sending
                                    textareaRef.current.style.height = "auto";
                                    setIsMultiLine(false);
                                    return; // ✅ Ensure no further code runs
                                }


                            }}
                            placeholder="Ask Product AI anything..."
                            className="flex-1 outline-none text-sm resize-none max-h-40 overflow-y-auto"
                            rows={1}
                        />

                        <div className='flex justify-end items-center gap-1'>
                            <button
                                onClick={() => navigate('/product-history')}
                                type='button'
                                className="p-1 hover:bg-gray-100 rounded-lg">
                                <History size={18} />
                            </button>

                            {/* Mic */}
                            <button
                                type="button"
                                className="p-2 hover:bg-gray-100 rounded-lg"
                            >
                                <Mic size={18} />
                            </button>

                            <button
                                type="submit"
                                disabled={isTyping || (!inputValue.trim() && !imageFile)}
                                className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
                            >
                                <Send size={16} />
                            </button>
                        </div>

                    </div>

                    <div className='text-[11px] md:text-sm text-gray-500 font-normal mt-0.5 w-full text-center'>
                        AI can sometimes make mistakes — please verify important details
                    </div>

                    {/* Dropdown Menu */}
                    {showOptions && (
                        <div className="absolute bottom-full mb-2 bg-white border shadow-md rounded-xl p-3 w-48 z-20 options-menu">
                            <ul className="text-sm space-y-2">
                                <li
                                    className="flex items-center gap-2 cursor-pointer hover:text-blue-600"
                                    onClick={() => {
                                        fileInputRef.current?.click();
                                        setShowOptions(false);
                                    }}
                                >
                                    <Image size={16} /> Upload Image
                                </li>
                            </ul>
                        </div>
                    )}
                    <input
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        onChange={(e) => {
                            handleImageUpload(e);
                            setShowOptions(false);
                        }}
                        className="hidden"
                    />
                </div>
            </form>
        </>
    );
};

export default ProductAi;
